import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Popup from 'reactjs-popup';
import AsyncSelect from 'react-select/async';
import axios from 'axios';

import useV3HoldDraft from '../../hooks/useV3HoldDraft';
import { offsetDateForBrowser } from '../../../../shared/timeZoneLogic';
import HoldPositionPicker from '../../../EventForm/components/HoldPositionPicker';
import RoomSelector from '../../../../shared/RoomSelector';
import useIsMobile from '../../../shared/hooks/useIsMobile';
import V3MobileBottomSheet from './V3MobileBottomSheet';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Quick-jump month + year dropdowns rendered ABOVE the calendar (instead of
// inside DayPicker's caption slot — that one collides with the day-grid
// layout). Selections set the parent's calendarStartDate, which DayPicker
// reflects via its controlled `month` prop. The default DayPicker caption +
// prev/next arrows still render below, so single-month stepping also works.
const HoldDateJump = ({ value, onChange }) => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 1; i <= currentYear + 5; i += 1) years.push(i);

  return (
    <div className="v3-hold-date-jump">
      <select
        aria-label="Month"
        value={value.getMonth()}
        onChange={(e) => onChange(new Date(value.getFullYear(), parseInt(e.target.value, 10)))}
      >
        {MONTH_NAMES.map((name, idx) => (
          <option key={name} value={idx}>{name}</option>
        ))}
      </select>
      <select
        aria-label="Year"
        value={value.getFullYear()}
        onChange={(e) => onChange(new Date(parseInt(e.target.value, 10), value.getMonth()))}
      >
        {years.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
};

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '40px',
    fontSize: '14px',
    borderRadius: '8px',
    borderColor: 'var(--od-c-d5d7da, #d5d7da)',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
  menu: (base) => ({ ...base, zIndex: 10000 }),
};

const fetchBuyerOptions = (teamId, inputValue) =>
  axios.get(`/teams/${teamId}/companies/autocomplete`, { params: { name: inputValue || '' } })
    .then(({ data }) => data || [])
    .catch(() => []);

// Remembers the last buyer Company used for a hold (scoped per team) so repeat
// hold creation doesn't require re-selecting it. Stored as { value, label }.
const lastBuyerKey = (teamId) => `opendate.v3HoldBuyer.${teamId}`;
const readLastBuyer = (teamId) => {
  try {
    const raw = window.localStorage.getItem(lastBuyerKey(teamId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && parsed.value ? parsed : null;
  } catch (e) {
    return null;
  }
};
const writeLastBuyer = (teamId, buyer) => {
  try {
    if (buyer && buyer.value) {
      window.localStorage.setItem(
        lastBuyerKey(teamId),
        JSON.stringify({ value: buyer.value, label: buyer.label }),
      );
    }
  } catch (e) {
    // localStorage may be unavailable (private mode / SSR) — non-fatal.
  }
};

const V3MobileCreateHoldSheet = ({
  open,
  onClose,
  team,
  offer,
  csrfToken,
  rooms,
  venueOwnerships,
  existingHoldPositions,
  onSubmit,
}) => {
  const [title, setTitle] = useState('');
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [calendarStartDate, setCalendarStartDate] = useState(new Date());
  const [makeUnavailable, setMakeUnavailable] = useState(false);

  const isMobile = useIsMobile();

  const draft = useV3HoldDraft({ team });

  const defaultRoomOptions = useMemo(() => {
    if (!rooms || !rooms.length) return [];
    const explicit = (offer?.room_ids || []).map((id) => rooms.find((r) => r.id === id)).filter(Boolean);
    const fallback = explicit.length
      ? []
      : rooms.filter((r) => {
        const ownership = (venueOwnerships || []).find((vo) => vo.id === r.venue_ownership_id);
        return ownership && ownership.venueable_id === offer?.venue_id;
      });
    return (explicit.length ? explicit : fallback).map((r) => ({
      value: r.id,
      label: r.name,
      room: r,
      venueOwnershipId: r.venue_ownership_id,
    }));
  }, [rooms, venueOwnerships, offer]);

  useEffect(() => {
    if (!open) return;
    setTitle(offer?.primary_performer?.artistable?.name || offer?.event_name || '');
    setSelectedRooms(defaultRoomOptions);
    // Buyer is a team Company (NOT current_user — that previously collided
    // User#id with Company#id and stamped an arbitrary Company onto the Hold).
    // Pre-fill with the last buyer used for a hold on this team so repeat holds
    // don't require re-selecting it; falls back to none when there's no history.
    setSelectedBuyer(readLastBuyer(team.id));
    setSubmitting(false);
    setMakeUnavailable(false);

    // Pre-populate the offer's already-held dates so creating a hold builds on
    // them instead of opening blank (and silently dropping them, since
    // handleCreateHold re-points the offer to the newly created hold).
    const seedHolds = (existingHoldPositions || [])
      .filter((p) => p && p.start_time)
      .map((p) => ({
        start_time: offsetDateForBrowser(new Date(p.start_time)),
        position: p.position,
        is_target: p.is_target,
      }));

    if (seedHolds.length > 0) {
      draft.seed(seedHolds);
      const earliest = seedHolds
        .map((h) => h.start_time)
        .sort((a, b) => a - b)[0];
      setCalendarStartDate(earliest);
    } else {
      draft.reset();
      setCalendarStartDate(new Date());
    }
    // We intentionally don't include `draft` in deps — it's a fresh hook
    // result on every render but `reset` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const buyerLoader = (input) => fetchBuyerOptions(team.id, input);

  const canSubmit = (
    !!title.trim()
    && selectedRooms.length > 0
    && draft.holdGroupDates.length > 0
    && !submitting
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    // Remember this buyer so the next hold pre-selects it.
    writeLastBuyer(team.id, selectedBuyer);
    try {
      await onSubmit({
        title: title.trim(),
        buyerId: selectedBuyer?.value || null,
        holdGroup: draft.holdGroup,
        selectedRooms,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  // The popup box portals outside .simple-offer-page; match the dark surface so
  // its rounded corners don't peek white behind the modal. Dark theme is keyed
  // off html[data-theme="dark"] (only set on dark simple pages).
  const isDarkTheme = typeof document !== 'undefined'
    && document.documentElement.getAttribute('data-theme') === 'dark';

  const desktopContentStyle = {
    border: 'none',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    width: 'min(640px, 92vw)',
    maxWidth: '640px',
    maxHeight: '90vh',
    overflow: 'auto',
    margin: 0,
    padding: 0,
    background: isDarkTheme ? '#181D25' : 'var(--od-c-ffffff, #fff)',
  };

  const desktopOverlayStyle = {
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const body = (
    <div
      className="add-artist-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-hold-title"
      data-testid="create-hold-sheet"
    >
      <header className="add-artist-modal__header">
        <div>
          <h2 className="add-artist-modal__title" id="create-hold-title">Create hold</h2>
          <p className="add-artist-modal__desc">Reserve dates for this artist on the calendar.</p>
        </div>
        <button
          type="button"
          className="add-artist-modal__close"
          aria-label="Close"
          onClick={onClose}
          disabled={submitting}
        >
          <i className="far fa-times" />
        </button>
      </header>

      <div className="add-artist-modal__body">
        <div className="hold-field">
          <label className="hold-field__label" htmlFor="create-hold-title-input">Event name</label>
          <input
            id="create-hold-title-input"
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter event name"
            disabled={submitting}
          />
        </div>

        <div className="hold-field">
          <label className="hold-field__label" htmlFor="create-hold-buyer">Buyer</label>
          <AsyncSelect
            inputId="create-hold-buyer"
            classNamePrefix="hold-select"
            cacheOptions
            defaultOptions
            styles={selectStyles}
            placeholder="Select buyer"
            loadOptions={buyerLoader}
            value={selectedBuyer}
            onChange={setSelectedBuyer}
            isDisabled={submitting}
            isClearable
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          />
        </div>

        <div className="hold-field">
          <span className="hold-field__label">Venue / rooms</span>
          <RoomSelector
            rooms={rooms || []}
            venueOwnerships={venueOwnerships || []}
            selectedRooms={selectedRooms}
            classNamePrefix="hold-select"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            onChange={(selected) => setSelectedRooms(selected || [])}
          />
        </div>

        <div className="hold-field">
          <span className="hold-field__label">Hold dates</span>
          <HoldDateJump value={calendarStartDate} onChange={setCalendarStartDate} />
          <HoldPositionPicker
            csrfToken={csrfToken}
            team={team}
            venueSelected={offer?.venue || null}
            holdGroup={draft.holdGroup}
            holdGroupCalendarDateClicked={draft.holdGroupCalendarDateClicked}
            holdGroupDates={draft.holdGroupDates}
            holdGroupHoldPositionUpdated={draft.holdGroupHoldPositionUpdated}
            holdGroupHoldRemoved={draft.holdGroupHoldRemoved}
            holdPositionTargetToggled={draft.holdPositionTargetToggled}
            fetchHoldAvailability={draft.fetchHoldAvailability}
            calendarRef={null}
            calendarStartDate={calendarStartDate}
            makeUnavailable={makeUnavailable}
            setMakeUnavailable={setMakeUnavailable}
            selectedRooms={selectedRooms}
          />
          <span className="hold-field__help">
            Tap dates on the calendar to add them. Assign a hold level to each.
          </span>
        </div>
      </div>

      <footer className="create-hold-modal__footer">
        <button
          type="button"
          className="btn-secondary"
          onClick={onClose}
          disabled={submitting}
          data-testid="create-hold-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          className="create-hold-modal__submit"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="create-hold-submit"
        >
          {submitting ? 'Creating…' : 'Create hold'}
        </button>
      </footer>
    </div>
  );

  if (isMobile) {
    return (
      <V3MobileBottomSheet
        open={open}
        onClose={onClose}
        closeOnDocumentClick={!submitting}
      >
        {body}
      </V3MobileBottomSheet>
    );
  }

  return (
    <Popup
      open={open}
      onClose={onClose}
      modal
      closeOnDocumentClick={!submitting}
      contentStyle={desktopContentStyle}
      overlayStyle={desktopOverlayStyle}
    >
      {body}
    </Popup>
  );
};

V3MobileCreateHoldSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  team: PropTypes.object.isRequired,
  offer: PropTypes.object,
  csrfToken: PropTypes.string.isRequired,
  rooms: PropTypes.array,
  venueOwnerships: PropTypes.array,
  existingHoldPositions: PropTypes.arrayOf(PropTypes.shape({
    start_time: PropTypes.string,
    position: PropTypes.number,
    is_target: PropTypes.bool,
  })),
  onSubmit: PropTypes.func.isRequired,
};

V3MobileCreateHoldSheet.defaultProps = {
  offer: null,
  rooms: [],
  venueOwnerships: [],
  existingHoldPositions: [],
};

export default V3MobileCreateHoldSheet;
